import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppDispatch } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView } from 'design-system';
import { HEIGHT, HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { savePotsOnboardingState } from 'features/pots/actions';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { roundUpOptions } from 'features/pots/types';
import createStyleSheets from 'utils/createStyleSheets';
const DEFAULT_MULTIPLIER = 1;
const BASE_EXAMPLE_PENCE = 60;
const AutosaveChooseRoundupContributions = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + HEIGHT + rem(8), paddingHorizontalStyle);
    const [multiplier, setMultiplier] = useState(DEFAULT_MULTIPLIER);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const onChangeRound = useCallback((round) => () => {
        setMultiplier(round);
    }, []);
    const exampleText = useMemo(() => {
        const pence = BASE_EXAMPLE_PENCE * multiplier;
        return pence >= 100 ? `£${(pence / 100).toFixed(2)}` : `${pence}p`;
    }, [multiplier]);
    const handleEnable = useCallback(() => {
        dispatch(savePotsOnboardingState({ roundUpMultiplier: multiplier }));
        navigateToNextScreen();
    }, [multiplier, navigateToNextScreen]);
    const handleNotNow = useCallback(() => {
        dispatch(savePotsOnboardingState({ roundUpMultiplier: null }));
        navigateToNextScreen();
    }, [navigateToNextScreen]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Boost your savings" subTitle={<Text TextThin-16 Secondary>
                Save your spare change automatically. We round up what you spend to the nearest{' '}
                <Text NumbersThin-16 Secondary>
                  £1
                </Text>{' '}
                and move the difference to your savings pot.
              </Text>}/>
          <CardView noMarginTop noMarginBottom withPadding>
            <Text Text-14>Use the multiplier to save faster:</Text>
            <View style={styles.buttonContainer}>
              {roundUpOptions.map((option) => (<Button flex square key={String(option)} title={`${option}x`} onPress={onChangeRound(option)} {...(multiplier === option ? { brandLight: true } : { secondary: true, gray: true })}/>))}
            </View>
          </CardView>
          <View style={styles.exampleContainer}>
            <Text TextThin-14 Secondary>
              Example: Spend{' '}
              <Text NumbersThin-14 Secondary>
                £3.40
              </Text>{' '}
              and we’ll save{' '}
              <Text NumbersThin-14 Secondary>
                {exampleText}
              </Text>
            </Text>
          </View>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title="Not now" brandReversed onPress={handleNotNow}/>
          <Button title="Enable Round ups" brand onPress={handleEnable}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    buttonContainer: {
        gap: rem(8),
        paddingTop: rem(16),
        flexDirection: 'row',
    },
    exampleContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(8),
    },
}));
export default AutosaveChooseRoundupContributions;
